import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { ObjectValues } from 'types';
import { YesNo } from './Fields';
import { EmailOrPhoneRequest } from './AuthBody';

export type BaseMixpanelEvent = {
  pageUrl: string;
};

export type LogoutProps = BaseMixpanelEvent;

export type ErrorViewedProps = BaseMixpanelEvent & {
  id: string;
  errorCode: string;
};

export type PageViewedProps = BaseMixpanelEvent & {
  page: string;
};

export type WitnessedProps = {
  userId: string;
  triggererId: string;
  isWitnessedEvent: boolean;
};

export type AhaMomentProps = {
  isOwner: boolean;
  userSide?: QAndASides;
};

export type TimeSpentProps = {
  timeSpentInSeconds: number;
};

export type DocumentEventProps = BaseMixpanelEvent &
  AhaMomentProps &
  TimeSpentProps & {
    documentName: string;
    dataRoomName?: string;
  };

export type WitnessedDocumentViewedEventProps = BaseMixpanelEvent &
  WitnessedProps &
  TimeSpentProps & {
    dataRoomName?: string;
    documentName: string;
  };

export type SearchInitiatedEventProps = BaseMixpanelEvent & {
  searchedTerm: string;
};

export type SecureLinkCreationInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    linkName: string;
    linkAccess: string;
    linkExpiration: string;
    linkProtection: string;
    remoteTermination: string;
    dataRoomId: string;
    dataRoomName?: string;
  };

export type ListingCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  listingName: string;
  industry: string;
  isPaid: boolean;
  listingPackage: string;
  listingType: string;
  mediaPackage?: string;
  outreach?: string;
};

export type ListingVisitedEventProps = BaseMixpanelEvent & {
  listingName: string;
  dealId: string;
  userSide: QAndASides;
  timeOnPageInSecs: number;
};

export type WitnessedListingVisitedEventProps = BaseMixpanelEvent &
  WitnessedProps & {
    listingName: string;
    dealId: string;
    timeOnPageInSecs: number;
  };

type PhoneMixpanelEventProps = BaseMixpanelEvent & {
  phoneNumber: string;
};

export type MobileVerificationInitiatedEventProps = PhoneMixpanelEventProps;

export type PhoneNumberSetupInitiatedEventProps = PhoneMixpanelEventProps;

export type LeadNotificationsModeChangedProps = BaseMixpanelEvent & {
  leadDrawerNotificationsMode: 'on' | 'off';
  dealId: string;
  dealName: string;
  leadId: string;
  leadName: string;
};

export const ENQUIRY_ORIGIN = {
  MANUAL: 'manual',
  ORGANIC: 'organic',
  SECURE_LINK: 'secure link',
  CONTACTS: 'contacts',
};

type EnquiryOrigin = ObjectValues<typeof ENQUIRY_ORIGIN>;

export const MANUAL_ENQUIRY_SUBCATEGORY = {
  NEW_LEAD: 'new lead',
  PREVIOUS_LEAD: 'previous lead',
} as const;

export type ManualEnquirySubcategory = ObjectValues<
  typeof MANUAL_ENQUIRY_SUBCATEGORY
>;

export type EnquiryCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  enquiryOrigin: EnquiryOrigin;
  manualEnquirySubcategory?: ManualEnquirySubcategory;
};

export type EnquiryWithdrawalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  leadId: string;
};

export const SIGNUP_METHOD = {
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
} as const;

export type SignupMethod = ObjectValues<typeof SIGNUP_METHOD>;

export const SIGNUP_SOURCE = {
  ORGANIC: 'organic',
  LEAD_INVITATION: 'lead_invitation',
  BROKER_INVITATION: 'broker_invitation',
  ENTERPRISE_INVITATION: 'enterprise_invitation',
  SECURE_LINK: 'secure_link',
} as const;

export type SignupSource = ObjectValues<typeof SIGNUP_SOURCE>;

export type SignupInitiatedProps = BaseMixpanelEvent & {
  socialSignup: boolean;
  signupSource: SignupSource;
  signupMethod?: SignupMethod;
  email?: string;
  phoneNumber?: string;
};

export type DealCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
};

export type SoftSignalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dataRoomId: string;
  documentId: string;
  reaction: string;
};

export type DocumentPublishedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    documentName: string;
    documentType: string;
    dataRoomId: string;
    dataRoomName: string;
  };

export type QuestionSubmissionInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionSubject: string;
    questionContent: string;
    questionPriority?: string;
    addedToFAQ?: boolean;
    assignee?: string;
    buysideBehalf?: string;
    relatedDocument?: string;
  };

export type QuestionReplyInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    answerContent: string;
    addedToFAQ: boolean;
    approvalModus: ApprovalModus;
  };

export const ANSWER_REVIEW_OUTPUT = {
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
} as const;

export type AnswerReviewOutput = ObjectValues<typeof ANSWER_REVIEW_OUTPUT>;

export type AnswerReviewInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    answerId: string;
    answerContent: string;
    addedToFAQ?: boolean;
    questionOutput: AnswerReviewOutput;
    approvalModus: ApprovalModus;
  };

export const APPROVAL_MODUS = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
} as const;

export type ApprovalModus = ObjectValues<typeof APPROVAL_MODUS>;

export type QuestionReviewInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    response?: string;
    questionOutput: AnswerReviewOutput;
    approvalModus: ApprovalModus;
    questionAssignee: string;
  };

export const DR_TOUR_SOURCE = {
  CARD: 'card',
  GLOBAL: 'global',
} as const;

export type DrTourSource = ObjectValues<typeof DR_TOUR_SOURCE>;

type DataRoomTourBaseProps = BaseMixpanelEvent & {
  dataRoomId: string;
  dataRoomName: string;
  userSide: QAndASides;
};

export type DataRoomTourTakenEventProps = DataRoomTourBaseProps & {
  drTourStartPage: string;
  drTourSource: DrTourSource;
};

export type DataRoomTourCardViewedEventProps = DataRoomTourBaseProps & {
  drCardPage: string;
  drCardTimeSpent: number;
  drInTourMode: YesNo;
};

export type DataRoomTourHiddenForeverEventProps = DataRoomTourBaseProps;

export const PASSWORD_FORGOTTEN_REROUTED_FROM = {
  LOGIN_PAGE: 'Login Page',
  SECURE_LINK_PAGE: 'Secure Link Page',
} as const;

export type PasswordForgottenReroutedFrom = ObjectValues<
  typeof PASSWORD_FORGOTTEN_REROUTED_FROM
>;

export type PasswordForgottenProps = BaseMixpanelEvent &
  EmailOrPhoneRequest & {
    reroutedFrom: PasswordForgottenReroutedFrom;
  };

export type MixpanelEventProps =
  | LogoutProps
  | ErrorViewedProps
  | PageViewedProps
  | DocumentEventProps
  | WitnessedDocumentViewedEventProps
  | SearchInitiatedEventProps
  | SecureLinkCreationInitiatedEventProps
  | ListingCreationInitiatedEventProps
  | ListingVisitedEventProps
  | WitnessedListingVisitedEventProps
  | MobileVerificationInitiatedEventProps
  | EnquiryCreationInitiatedEventProps
  | EnquiryWithdrawalInitiatedEventProps
  | SignupInitiatedProps
  | DealCreationInitiatedEventProps
  | SoftSignalInitiatedEventProps
  | DocumentPublishedEventProps
  | QuestionSubmissionInitiatedEventProps
  | QuestionReplyInitiatedEventProps
  | AnswerReviewInitiatedEventProps
  | QuestionReviewInitiatedEventProps
  | PhoneNumberSetupInitiatedEventProps
  | LeadNotificationsModeChangedProps
  | DataRoomTourTakenEventProps
  | DataRoomTourCardViewedEventProps
  | DataRoomTourHiddenForeverEventProps
  | PasswordForgottenProps;
